import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSiteStore } from '@/stores/siteStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SiteFormDialog } from '@/components/sites/SiteFormDialog';
import { SiteMap } from '@/components/sites/SiteMap';
import { Site, SiteInsert } from '@/types/site';
import { Plus, MapPin, Pencil, Trash2, Eye, Shield } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function SitesPage() {
  const navigate = useNavigate();
  const { sites, loading, loadSites, addSite, updateSite, deleteSite } = useSiteStore();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [deletingSiteId, setDeletingSiteId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleCreateSite = async (data: SiteInsert) => {
    console.log('handleCreateSite called with:', data);
    try {
      const result = await addSite(data);
      console.log('addSite result:', result);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Error in handleCreateSite:', error);
    }
  };

  const handleUpdateSite = async (data: SiteInsert) => {
    if (!editingSite) return;
    await updateSite(editingSite.id, data);
    setEditingSite(null);
  };

  const handleDeleteSite = async () => {
    if (!deletingSiteId) return;
    await deleteSite(deletingSiteId);
    setDeletingSiteId(null);
  };

  const handleViewAssets = (siteId: string) => {
    navigate(`/assets?site=${siteId}`);
  };


  const getCriticalityColor = (criticality?: string) => {
    switch (criticality) {
      case 'High': return 'destructive';
      case 'Medium': return 'default';
      case 'Low': return 'secondary';
      default: return 'outline';
    }
  };

  const getVulnerabilityColor = (level?: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (level) {
      case 'Critical': return 'destructive';
      case 'High': return 'destructive';
      case 'Medium': return 'secondary';
      case 'Low': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Sites</h1>
          <p className="text-muted-foreground">Manage your organization's physical locations</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            onClick={() => setViewMode('table')}
          >
            Table
          </Button>
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            onClick={() => setViewMode('map')}
          >
            <MapPin className="h-4 w-4 mr-2" />
            Map
          </Button>
          <Button onClick={() => { console.log('Add Asset button clicked'); setCreateDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <Card>
          <CardHeader>
            <CardTitle>Site Locations</CardTitle>
            <CardDescription>
              Sites with GPS coordinates are shown on the map
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SiteMap
              sites={sites}
              onSiteClick={(site) => setEditingSite(site)}
              className="h-[600px]"
            />
            <p className="text-sm text-muted-foreground mt-4">
              Note: Configure your Mapbox API token in Supabase Edge Function Secrets for full map functionality
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <CardHeader>
            <CardTitle>All Sites ({sites.length})</CardTitle>
            <CardDescription>
              Overview of all registered sites in your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading sites...</p>
            ) : sites.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No sites yet. Create your first site to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Criticality</TableHead>
                    <TableHead>Risk Exposure</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sites.map((site) => (
                    <TableRow key={site.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Physical</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{site.name}</TableCell>
                      <TableCell>
                        {site.site_code && (
                          <Badge variant="outline">{site.site_code}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {site.address || '-'}
                      </TableCell>
                      <TableCell>
                        {site.criticality && (
                          <Badge variant={getCriticalityColor(site.criticality)}>
                            {site.criticality}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {site.vulnerability_level ? (
                          <div className="flex items-center gap-1.5">
                            <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                            <Badge variant={getVulnerabilityColor(site.vulnerability_level)}>
                              {site.vulnerability_level} ({site.vulnerability_score || 0})
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{site.owner || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewAssets(site.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingSite(site)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingSiteId(site.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <SiteFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateSite}
      />

      {/* Edit Dialog */}
      <SiteFormDialog
        open={!!editingSite}
        onOpenChange={(open) => !open && setEditingSite(null)}
        onSubmit={handleUpdateSite}
        site={editingSite}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingSiteId} onOpenChange={(open) => !open && setDeletingSiteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Site</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this site? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSite}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
